import { useCallback, useContext, useState, useRef, useMemo } from 'react';
import { useGlobalEnvConfig } from "@environment/GlobalEnvConfigProvider";
import { Trans } from '@lingui/macro';

import { BasicData } from '@App';
import { getTextColorClass, shouldbeBranded, scrollTo, validateEmail, validatePhonenumber, getColor, isLite } from '@utils/Utils';
import { useItineraryService } from '@services/itinerary';
import Input from '@components/form/input';
import SpinnerComponent from '@components/spinner';
import About from '@components/about';
import TextArea from '@components/form/text-area';
import Radio, { RadioOption } from '@components/form/radio';
import DateInputs from '@components/form/date-inputs';
import Recaptcha from "react-google-recaptcha";
import LottiePlayer from 'react-lottie-player';
import successEnvelopeAnimation from '@assets/json/enquire-envelope.json';
import Branding from './branding';
import { useDispatch, useSelector } from 'react-redux';
import { setEnquiryValue } from '@utils/redux/slices/enquiry';
import { useIsMobile } from '@utils/hooks/useIsMobile';

import './style.scss';

const EnquiryForm = ({closeModal, closeSlideUp}) => {
    const { REACT_APP_RECAPTCHA_SITEKEY } = useGlobalEnvConfig();
    const { sendEnquiry  } = useItineraryService();
    const { id, branding, language, unique_enquiry_url, theme, operator_tier } = useContext(BasicData);
    const mobile = useIsMobile();

    const colors = getColor(theme);

    const details = useSelector(state => state.enquiry.value);
    const dispatch = useDispatch();

    const reCaptchaRef = useRef(null);
    const [reCaptchaValue, setReCaptchaValue] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const [recaptchaRequired, setRecaptchaRequired] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        name: null,
        email: null,
        telephone: null
    })

    const showBranding = useMemo(() => isLite(operator_tier) || shouldbeBranded(), [operator_tier]);

    const hideInputs = useMemo(() => {
        if (submitSuccess) {
            return "hide";
        }
        return "";
    }, [submitSuccess])

    const showEnquiryForm = useMemo(() => {
        if (isLite(operator_tier)) return true;
        if (theme?.enquiry_button_label === "Hidden" || theme?.enquiry_button_label === "Hide" || !branding) return false;
        if (unique_enquiry_url && unique_enquiry_url?.length > 0) return false;
        if (branding?.enquiry_url && branding?.enquiry_url?.length > 0) return false;
        return true;
    }, [branding, unique_enquiry_url, theme, operator_tier])

    const onReCaptchaChange = useCallback((value) => {
        setReCaptchaValue(value);
        setRecaptchaRequired(value === '');
    }, [])

    const onReCaptchaExpired = useCallback(() => {
        setRecaptchaRequired(false);
        setReCaptchaValue(null);
    }, [])

    const isValid = useMemo(() => {
        if (!details.name.length) return false;
        if (!details.email.length || !validateEmail(details.email)) return false;
        if (details.telephone.length < 7 || !validatePhonenumber(details.telephone)) return false;
        if (!reCaptchaValue) return false;
        return true;
    }, [details, reCaptchaValue])

    const onBlur = (field) => () => {
        if (field === "email") {
            if (details.email.length && !validateEmail(details.email)) {
                setErrorMessage(prev => ({ ...prev, email: "Email must be in a valid format. Eg: name@email.com" }));
            }
        } else if (field === "telephone") {
            if (details.telephone.length) {
                if (!validatePhonenumber(details.telephone)) {
                    setErrorMessage(prev => ({ ...prev, telephone: "Please use numerical values only" }));
                } else if (details.telephone.length < 7) {
                    setErrorMessage(prev => ({ ...prev, telephone: "Must be at least 7 digits" }));
                }
            }
        }
    }

    const onChange = (field) => (value) => {
        setErrorMessage(prev => ({ ...prev, [field]: null }));
        dispatch(setEnquiryValue({
            ...details,
            [field]: value
        }))
    }

    const onSubmit = useCallback(() => {
        scrollTo("enquiry-form");
        setSubmitSuccess(false);
        setSubmitError(false);

        if (!isValid) {
            if (!details.name.length) setErrorMessage(prev => ({ ...prev, name: "This is a required field" }));
            if (!details.email.length) setErrorMessage(prev => ({ ...prev, email: "This is a required field" }));
            if (details.telephone.length < 7) setErrorMessage(prev => ({ ...prev, telephone: "This is a required field" }));
            if (!reCaptchaValue) setRecaptchaRequired(true);
        }
        else {
            reCaptchaRef.current.reset();
            setIsLoading(true);

            sendEnquiry({
                ...details,
                number_of_adults: details.number_of_adults === '' ? 0 : details.number_of_adults,
                number_of_children: details.number_of_children === '' ? 0 : details.number_of_children
            }, id, reCaptchaValue).then(response => {
                if (response.ok) {
                    setSubmitSuccess(true)
                } else {
                    setSubmitError(true)
                }

                setIsLoading(false)
            }).catch(() => {
                setIsLoading(false)
                setSubmitError(true)
            })
        }
    }, [details, id, isValid, reCaptchaValue, sendEnquiry])

    return (
        <div className={`enquiry-form-wrapper ${isLite(operator_tier) ? 'lite h-full overflow-y-auto' : ''}`}>
            <div id='enquiry-form' className={`enquiry-form ${isLite(operator_tier) ? 'lite-enquiry-form h-full' : ''}`}>             
                {(showBranding && ((!isLite(operator_tier)) || (isLite(operator_tier) && !mobile))) ? <Branding /> : null}

                {showEnquiryForm ? (
                    <div className={`form ${hideInputs} ${isLite(operator_tier) ? 'lite-form' : ''}`}>
                        {(!submitSuccess && ((!isLite(operator_tier)) || (isLite(operator_tier) && !mobile))) && <h3 style={{color: '#5E5873'}}><Trans>Enquire about this trip</Trans></h3>}
                        {submitSuccess && (
                            <>
                                {isLite(operator_tier) ? (
                                    <div className={`flex flex-col justify-center items-center ${submitSuccess ? 'success' : 'success hide'}`}>
                                            {!isLoading && submitSuccess && (
                                                <div>
                                                    <LottiePlayer
                                                    animationData={successEnvelopeAnimation}
                                                    play
                                                    loop={false}
                                                    style={{
                                                        width: '100%',
                                                        minWidth: '192px',
                                                        maxWidth: '192px',
                                                        marginTop: '20px'
                                                    }}
                                                />
                                                </div>
                                            )}
                                        <h3><Trans>Thank you</Trans>!</h3>
                                        <p><Trans>Your enquiry has been sent successfully!</Trans></p>
                                        <p><Trans>An agent will be in contact shortly</Trans>.</p>

                                        {mobile ? (
                                            <button className={`btn text-white ${isLite(operator_tier) ? 'lite-btn' : ''}`} onClick={() => closeSlideUp(false)}>
                                                <i className="icon-arrow-left light-text flex justify-center items-center rounded-full cursor-pointer"></i>
                                                <Trans>Back</Trans>
                                            </button>
                                        ) : (
                                            <button className={`btn text-white ${isLite(operator_tier) ? 'lite-btn' : ''}`} onClick={() => closeModal()}>
                                                <i className="icon-arrow-left light-text flex justify-center items-center rounded-full cursor-pointer"></i>
                                                <Trans>Back</Trans>
                                            </button>
                                        )}
                                        
                                    </div>
                                ) : (
                                    <div className="flex flex-col justify-center items-center text-center">
                                        {!isLoading && submitSuccess && (
                                            <LottiePlayer
                                                animationData={successEnvelopeAnimation}
                                                play
                                                loop={false}
                                                style={{
                                                    width: '100%',
                                                    minWidth: '192px',
                                                    maxWidth: '192px',
                                                    marginTop: '20px'
                                                }}
                                            />
                                        )}
        
                                        <div className={submitSuccess ? 'success' : 'success hide'}>
                                            <h3><Trans>Thank you</Trans>!</h3>
                                            <p><Trans>Your enquiry has been sent successfully!</Trans></p>
                                            <p><Trans>An agent will be in contact shortly</Trans>.</p>
                                        </div> 
                                    </div>
                                )}                         
                            </>
                        )}
                        {submitError &&
                            <div className='error-banner'>
                                <i className='icon icon-info'></i><h5><Trans>Something went wrong. Please try again.</Trans></h5>
                            </div>
                        }
                        <div className='form-inputs'>
                            <Input disabled={isLoading} id="txt-name" required label="Name" value={details.name} type="text" onChange={onChange("name")} error={errorMessage.name} />
                            <Input disabled={isLoading} id="txt-email" required label="Email" value={details.email} type="email" onChange={onChange("email")} error={errorMessage.email} onBlur={onBlur("email")} />
                            <Input disabled={isLoading} id="txt-phone" required label="Telephone" value={details.telephone} type="tel" onChange={onChange("telephone")} error={errorMessage.telephone} onBlur={onBlur("telephone")} />
                            <Radio disabled={isLoading} heading="Contact Preference" onChange={onChange("contact_preference")} value={details.contact_preference}>
                                <RadioOption disabled={isLoading} value="email" text="Email" />
                                <RadioOption disabled={isLoading} value="phone" text="Phone" />
                            </Radio>
                            <div className="grid">
                                <Input disabled={isLoading} id="txt-num-of-adults" label="Number of Adults" value={details.number_of_adults} min={0} type="number" onChange={onChange("number_of_adults")} />
                                <Input disabled={isLoading} id="txt-num-of-children" label="Number of Children" value={details.number_of_children} min={0} type="number" onChange={onChange("number_of_children")} />
                            </div>
                            <div className="grid">
                                <DateInputs disabled={isLoading} details={details} setDetails={val => dispatch(setEnquiryValue(val))} />
                            </div>
                            <TextArea disabled={isLoading} label="Message" value={details.message} onChange={onChange("message")} />
                            <Recaptcha
                                className='recaptcha'
                                sitekey={REACT_APP_RECAPTCHA_SITEKEY}
                                ref={reCaptchaRef}
                                onChange={onReCaptchaChange}
                                onExpired={onReCaptchaExpired}
                                hl={language}

                            />
                            {recaptchaRequired && <span className='recaptcha-error'><Trans>Please fill out this field</Trans></span>}
                            <div className={`submit ${isLite(operator_tier) ? 'lite' : ''}`}>
                                <button disabled={isLoading} className={`btn ${getTextColorClass(colors.button_color)} ${isLite(operator_tier) ? 'lite-btn' : ''}`} onClick={onSubmit}>
                                    <span className={isLoading ? 'hide' : ''}>
                                        <Trans>SEND</Trans>
                                    </span>
                                    <span className={!isLoading ? 'loading hide' : 'loading '}>
                                        <SpinnerComponent />
                                    </span>
                                </button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div>
                        <About />
                    </div>
                )}
            </div>
            {showEnquiryForm && !isLite(operator_tier) && (
                <About />
            )}
        </div>

    )
}

export default EnquiryForm